// Run after Vue mounts, so the active-credit progress bar can receive the
// celebrations. Every pending celebration plays in turn —one confetti burst
// after another— and is acknowledged when it starts: whatever the user leaves
// before seeing stays pending on the server for the next visit.
const KINDS = ['installment_paid', 'level_up'];
// Floor between bursts: the progress bar needs two frames plus its 1200 ms
// advance, and on 120+ Hz screens a burst ends sooner than that.
const MIN_GAP_MS = 1500;
// A burst promise that never settles must not stall the queue.
const MAX_GAP_MS = 6000;

const wait = ms => new Promise(resolve => { window.setTimeout(resolve, ms); });

// canvas-confetti pauses in a hidden tab: wait instead of spending the
// celebration where nobody can see it.
const whenVisible = () => new Promise(resolve => {
  const onChange = () => {
    if (document.visibilityState === 'hidden') return;
    document.removeEventListener('visibilitychange', onChange);
    resolve();
  };
  document.addEventListener('visibilitychange', onChange);
});

export default function readyCreditCelebration() {
  const marker = document.getElementById('credit-celebration');
  if (!marker || marker.dataset.started === 'true' || typeof window.confettiSpawn !== 'function') return;

  const token = document.querySelector('meta[name="csrf-token"]')?.content;
  const url = marker.dataset.celebrationUrl;
  if (!token || !url) return;

  let celebrations;
  try {
    celebrations = JSON.parse(marker.dataset.celebrations);
  } catch (_) {
    return;
  }
  if (!Array.isArray(celebrations)) return;
  celebrations = celebrations.filter(celebration => celebration && KINDS.includes(celebration.kind));
  if (!celebrations.length) return;
  // The collect modal covers the confetti canvas and reloads the page when it
  // closes: the sequence plays after that reload, not hidden under the modal.
  if (document.getElementById('credits-collect-toggle')?.checked) return;

  marker.dataset.started = 'true';
  const acknowledge = celebration => fetch(url, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
    body: JSON.stringify(celebration)
  }).catch(() => {});
  // The progress bar replays one step per event, so it moves once per credit:
  // with the last installment of that credit in the queue.
  const lastOfLoan = new Map();
  celebrations.forEach((celebration, index) => {
    if (celebration.kind === 'installment_paid') lastOfLoan.set(celebration.loan_id, index);
  });
  const advanceProgress = (celebration, index) => {
    if (celebration.kind !== 'installment_paid' || lastOfLoan.get(celebration.loan_id) !== index) return;
    document.dispatchEvent(new CustomEvent('credits:installment-celebration', { detail: celebration }));
  };

  // Nothing to watch with reduced motion: acknowledge everything at once.
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    celebrations.forEach((celebration, index) => {
      advanceProgress(celebration, index);
      acknowledge(celebration);
    });
    return;
  }

  // Leaving the page ends the queue for good. Back restores it from bfcache
  // with its waits already over while the private-page guard hides it and
  // reloads it: a burst resumed there would be acknowledged unseen and missing
  // from the reload. Whatever did not play stays pending on the server.
  let pageGone = false;
  window.addEventListener('pagehide', () => { pageGone = true; }, { once: true });

  (async () => {
    for (const [index, celebration] of celebrations.entries()) {
      // Only a hidden tab waits: the first burst stays synchronous with ready().
      if (document.visibilityState === 'hidden') await whenVisible();
      // Both waits, the gap and the visibility, can end on a restored page.
      if (pageGone) return;
      const burst = Promise.resolve(window.confettiSpawn({ x: 0.5, y: 0.4 })).catch(() => {});
      advanceProgress(celebration, index);
      acknowledge(celebration);
      if (index < celebrations.length - 1) {
        await Promise.race([Promise.all([burst, wait(MIN_GAP_MS)]), wait(MAX_GAP_MS)]);
      }
    }
  })();
}
